import { useEffect, useState } from "react";
import {
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { postData } from "../../Utils/Http/api";
import { getTipoCierreDescripcion } from "../../Utils/TipoCierre";
import { CabeceraOrdenable, ordenarFilas, useOrdenTabla } from "./informeDisparadorComun";

// Estado de la última propuesta del caso. Las finalizadas no llevan marca: es lo normal.
const ESTADOS_PROPUESTA = {
  ACTIVA: { label: "En curso", color: "info" },
  PAUSADA: { label: "Pausada", color: "warning" },
  LIBERADA: { label: "Liberada", color: "default" },
};

// Color del cierre según cómo terminó el caso.
const COLOR_CIERRE = { 1: "default", 2: "success", 3: "error", 4: "info" };

const formatearDia = (iso) => {
  const [anio, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${anio}`;
};

// Las fechas llegan con la hora del servidor, sin zona: se muestran tal cual.
const fechaLegible = (valor) => {
  if (!valor) return "";
  const fecha = new Date(valor);
  return Number.isNaN(fecha.getTime()) ? "" : fecha.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const numero = (valor) => (valor ?? 0).toLocaleString("es-AR");

// Por qué valor se ordena cada columna al tocar su título. Los casos vienen del más reciente al más antiguo; el tercer toque vuelve a ese orden.
const ORDEN_CASOS = {
  caso: (c) => c.idCaso,
  estado: (c) => c.estado,
};

// Los casos que suman en la fila de un gestor de la tabla de posiciones, con su último estado, para analizar cómo terminó la
// gestión. Usa el mismo período de la pantalla y el mismo criterio (tomados o solo finalizados) con que se ordenó el ranking.
function RankingCasosGestor({ open, onClose, desde, hasta, gestor, soloFinalizados }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const orden = useOrdenTabla();

  const idUsuario = gestor?.idUsuario;

  useEffect(() => {
    if (!open || !idUsuario) return undefined;
    let cancelado = false;
    const cargar = async () => {
      setCargando(true);
      setError("");
      setDatos(null);
      try {
        const res = await postData("/InformeDisparadorRankingCasos", { desde, hasta, idUsuario, soloFinalizados });
        if (!cancelado) setDatos(res?.value ?? null);
      } catch (ex) {
        if (!cancelado) setError(ex?.detail || ex?.message || "No se pudieron obtener los casos del gestor");
      } finally {
        if (!cancelado) setCargando(false);
      }
    };
    cargar();
    return () => {
      cancelado = true;
    };
  }, [open, idUsuario, desde, hasta, soloFinalizados]);

  const cabecera = (texto, clave, ancho) => <CabeceraOrdenable texto={texto} tabla={orden} clave={clave} ancho={ancho} />;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Casos de {gestor?.nombre}
        <MDTypography display="block" variant="caption" color="text" fontWeight="regular">
          Del {formatearDia(desde)} al {formatearDia(hasta)} · {soloFinalizados ? "solo casos finalizados" : "todos los casos tomados"}
        </MDTypography>
      </DialogTitle>
      <DialogContent dividers>
        <MDBox display="flex" flexDirection="column" gap={2}>
          {datos && (
            <MDTypography variant="caption" fontWeight="bold" color="dark">
              {numero(datos.asignaciones)} {datos.asignaciones === 1 ? "asignación" : "asignaciones"} de {numero(datos.casosDistintos)}{" "}
              {datos.casosDistintos === 1 ? "caso distinto" : "casos distintos"} · {numero(datos.puntos)} puntos
            </MDTypography>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {datos?.truncado && (
            <Alert severity="info">Se muestran los {datos.casos.length} casos asignados más recientemente. Acotá las fechas para ver el resto.</Alert>
          )}

          {cargando && (
            <MDBox display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </MDBox>
          )}

          {!cargando && datos && (
            <TableContainer sx={{ maxHeight: "55vh" }}>
              <Table size="small" stickyHeader>
                <TableHead sx={{ display: "table-header-group", p: 0 }}>
                  <TableRow>
                    {cabecera("Caso", "caso", "22%")}
                    {cabecera("Último estado", "estado")}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordenarFilas(datos.casos, orden[0], ORDEN_CASOS).map((c) => {
                    const propuesta = ESTADOS_PROPUESTA[c.estadoPropuesta];
                    const cerrado = c.tipoCierre > 0;
                    return (
                      <TableRow key={c.idCaso} hover>
                        <TableCell>
                          <MDBox lineHeight={1.2}>
                            <MDTypography variant="button" fontWeight="bold" color="dark">
                              #{c.idCaso}
                            </MDTypography>
                            {(c.veces > 1 || propuesta) && (
                              <MDBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap" mt={0.3}>
                                {c.veces > 1 && (
                                  <MDTypography variant="caption" color="text">
                                    asignado {c.veces} veces
                                  </MDTypography>
                                )}
                                {propuesta && <Chip size="small" color={propuesta.color} label={propuesta.label} />}
                              </MDBox>
                            )}
                          </MDBox>
                        </TableCell>
                        <TableCell>
                          {c.estado ? (
                            <MDBox lineHeight={1.2}>
                              <MDTypography display="block" variant="button" fontWeight="medium">
                                {c.estado}
                              </MDTypography>
                              <MDTypography display="block" variant="caption" color="text">
                                {c.ultimoEvento}
                                {c.fechaUltimoEstado ? ` · ${fechaLegible(c.fechaUltimoEstado)}` : ""}
                              </MDTypography>
                            </MDBox>
                          ) : (
                            <MDTypography variant="caption" color="text">
                              Sin estado
                            </MDTypography>
                          )}
                          {cerrado && (
                            <Chip size="small" variant="outlined" color={COLOR_CIERRE[c.tipoCierre] ?? "default"} label={getTipoCierreDescripcion(c.tipoCierre)} sx={{ mt: 0.5 }} />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {datos.casos.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <MDTypography variant="button" color="text">
                          No hay casos para mostrar.
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="info" onClick={onClose}>
          Cerrar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default RankingCasosGestor;
